// src/feature-module/reports/Transactions.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";

import Table from "../../core/pagination/datatable";
import { setToogleHeader } from "../../core/redux/action";
import { getAllTransactions } from "../../api/transactionHistory";

import {
  ChevronUp,
  RotateCcw,
  Download,
} from "feather-icons-react/build/IconComponents";
import {
  getCurrentUserPerms,
  hasWrite,
  isCurrentUserAdmin,
} from "../../utils/perm";

const placeholderImg = "/assets/img/by-default.png";

// --- image URL helpers ---
const isHttpUrl = (v) => typeof v === "string" && /^https?:\/\//i.test(v || "");
const looksLikeCatalog = (v) =>
  typeof v === "string" && /^\/?(catalog|catelog)\//i.test(v || "");
const normalizeKey = (s = "") => {
  let v = String(s)
    .trim()
    .replace(/^\/+/, "")
    .replace(/^catelog\//i, "catalog/");
  if (!/^catalog\//i.test(v)) v = `catalog/${v.split("/").pop()}`;
  return v;
};
const toPublicUrl = (v) => {
  if (!v) return "";
  if (isHttpUrl(v)) return v;
  if (looksLikeCatalog(v)) {
    const base = (process.env.REACT_APP_API_IMAGE_BASE_URL || "").replace(
      /\/+$/,
      "",
    );
    return base ? `${base}/${normalizeKey(v)}` : normalizeKey(v);
  }
  return v; // plain filenames or data URLs
};

const isPermissionDeniedError = (err) => {
  const status = err?.response?.status;
  if (!(status === 401 || status === 403)) return false;
  const msg = String(
    err?.response?.data?.message || err?.response?.data?.error || ""
  ).toLowerCase();
  return (
    status === 403 ||
    /permission|forbidden|access\s*denied|not\s*allowed|unauthorized/.test(msg)
  );
};

const Transactions = () => {
  const dispatch = useDispatch();
  const headerCollapsed = useSelector((s) => s.rootReducer.toggle_header);
  const user = useSelector((s) => s?.auth?.user);
  const userPerms = getCurrentUserPerms(user?.permissions || []);
  const canExportTransactions =
    isCurrentUserAdmin(user) ||
    userPerms.includes("*") ||
    hasWrite(userPerms, "transactionHistory") ||
    hasWrite(userPerms, "transactionReports");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // local search (sits beside Export)
  const [searchText] = useState("");

  const getLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

  const L = useMemo(() => {
    const labels = {
      en: {
        fileName: "transactions_en.xlsx",
        sheetName: "Transactions",
        orderId: "Order ID",
        type: "Type",
        product: "Product",
        customer: "Customer",
        itemsCount: "Items",
        totalAmount: "Total",
        receivedAmount: "Received",
        payingAmount: "Paying",
        change: "Change",
        paymentReceiver: "Payment Receiver",
        paymentNote: "Payment Note",
        createdAt: "Date",
        typeCash: "Cash",
        typeCard: "Card",
        exportTooltip: "Export to Excel",
        search: "Search",
        export: "Export",
      },
      ar: {
        fileName: "transactions_ar.xlsx",
        sheetName: "المعاملات",
        orderId: "رقم الطلب",
        type: "الطريقة",
        product: "المنتج",
        customer: "العميل",
        itemsCount: "العناصر",
        totalAmount: "الإجمالي",
        receivedAmount: "المستلم",
        payingAmount: "المدفوع",
        change: "الباقي",
        paymentReceiver: "مستلم الدفع",
        paymentNote: "ملاحظة الدفع",
        createdAt: "التاريخ",
        typeCash: "نقدًا",
        typeCard: "بطاقة",
        exportTooltip: "تصدير إلى إكسل",
        search: "بحث",
        export: "تصدير",
      },
    };
    return labels[getLang()] || labels.en;
  }, []);

  // Load transactions
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const resp = await getAllTransactions();
        const data = resp?.transactions ?? resp ?? [];

        if (!alive) return;

        const formatted = (data || []).map((t) => {
          // Build a quick product lookup for this transaction
          // Build product lookup
          const productMap = new Map(
            (t.productDocs || t.products || []).map((p) => [
              String(p._id),
              { name: p.productName, image: p.images },
            ]),
          );

          // Build variant lookup
          const variantMap = new Map(
            (t.variantDocs || []).map((v) => [
              String(v._id),
              { image: v.image },
            ]),
          );

          const items = Array.isArray(t.items) ? t.items : [];
          const itemCount = items.reduce(
            (sum, it) => sum + Number(it.quantity || 0),
            0,
          );

          // Build a display array for products on this transaction
          const products = items.map((it) => {
            const pid = String(it.productId);

            // Try product first
            const productDoc = productMap.get(pid);

            // Try variant if not found
            const variantDoc = variantMap.get(pid);

            const localizedName =
              productDoc?.name?.[getLang()] ||
              productDoc?.name?.en ||
              productDoc?.name?.ar ||
              it.name ||
              "";

            const imgRaw =
              variantDoc?.image ||
              (Array.isArray(productDoc?.image)
                ? productDoc.image[0]
                : productDoc?.image) ||
              "";

            const imageUrl = toPublicUrl(imgRaw);

            return {
              id: String(it.productId),
              name: localizedName,
              image: imageUrl,
            };
          });

          const createdAt = t.createdAt ? new Date(t.createdAt) : null;

          return {
            ...t,
            id: t._id || t.id,
            orderId: t.orderId,
            type: t.type,
            products, // ⬅️ used by the Product column
            customer: t.customerName || t.customerId || "-",
            itemsCount: itemCount,
            totalAmount: Number(t.totalAmount || 0),
            receivedAmount: Number(t.receivedAmount || 0),
            payingAmount: Number(t.payingAmount || 0),
            change: Number(t.change || 0),
            paymentReceiver: t.paymentReceiver || "",
            paymentNote: t.paymentNote || "",
            createdAt,
          };
        });

        setRows(formatted);
      } catch (e) {
        if (!alive) return;
        setRows([]);
        if (isPermissionDeniedError(e)) {
          toast.error(`❌ ${window.translate("You don't have permission to access order report")}`);
        } else {
          toast.error(`❌ ${window.translate("Failed to load order report")}`);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Client-side filter
  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const productNames = (r.products || []).map((p) => p.name).join(", ");
      const fields = [
        r.orderId,
        r.type,
        r.customer,
        r.paymentReceiver,
        r.paymentNote,
        productNames,
        r.totalAmount?.toString(),
        r.receivedAmount?.toString(),
        r.payingAmount?.toString(),
        r.change?.toString(),
        r.createdAt ? new Date(r.createdAt).toLocaleString() : "",
      ];
      return fields.some((f) =>
        String(f || "")
          .toLowerCase()
          .includes(q),
      );
    });
  }, [rows, searchText]);

  // Excel export
  const handleExportExcel = () => {
    if (!canExportTransactions) {
      toast.error(`❌ ${window.translate("You don't have permission to export reports")}`);
      return;
    }

    const hdr = {
      orderId: L.orderId,
      type: L.type,
      product: L.product,
      customer: L.customer,
      itemsCount: L.itemsCount,
      totalAmount: L.totalAmount,
      receivedAmount: L.receivedAmount,
      payingAmount: L.payingAmount,
      change: L.change,
      paymentReceiver: L.paymentReceiver,
      paymentNote: L.paymentNote,
      createdAt: L.createdAt,
    };

    const toExport = filteredRows.map((r) => ({
      [hdr.orderId]: r.orderId,
      [hdr.type]:
        r.type === "cash"
          ? L.typeCash
          : r.type === "card"
            ? L.typeCard
            : r.type || "",
      [hdr.product]: (r.products || []).map((p) => p.name).join(", "),
      [hdr.customer]: r.customer || "",
      [hdr.itemsCount]: Number(r.itemsCount || 0),
      [hdr.totalAmount]: Number(r.totalAmount || 0),
      [hdr.receivedAmount]: Number(r.receivedAmount || 0),
      [hdr.payingAmount]: Number(r.payingAmount || 0),
      [hdr.change]: Number(r.change || 0),
      [hdr.paymentReceiver]: r.paymentReceiver || "",
      [hdr.paymentNote]: r.paymentNote || "",
      [hdr.createdAt]: r.createdAt
        ? new Date(r.createdAt).toLocaleString()
        : "",
    }));

    const ws = XLSX.utils.json_to_sheet(toExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, L.sheetName);
    XLSX.writeFile(wb, L.fileName);
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        title: window.translate("Order ID"),
        dataIndex: "orderId",
        sorter: (a, b) => (a.orderId || "").localeCompare(b.orderId || ""),
      },
      {
        title: window.translate("Type"),
        dataIndex: "type",
        sorter: (a, b) => (a.type || "").localeCompare(b.type || ""),
        render: (text) =>
          text === "cash"
            ? window.translate("Cash")
            : text === "card"
              ? window.translate("Card")
              : text || "-",
      },
      // 🆕 Product column (image + name of first product, +N badge if many)
      {
        title: L.product,
        dataIndex: "products",
        sorter: (a, b) =>
          (a.products?.[0]?.name || "").localeCompare(
            b.products?.[0]?.name || "",
          ),
        render: (arr) => {
          if (!arr?.length) return "-";
          const first = arr[0];
          const extra = arr.length - 1;
          return (
            <div className="d-flex align-items-center gap-2">
              <img
                src={first.image || placeholderImg}
                alt={first.name || "product"}
                style={{
                  width: 28,
                  height: 28,
                  objectFit: "cover",
                  borderRadius: 4,
                }}
                onError={(e) => {
                  e.currentTarget.src = placeholderImg;
                }}
              />
              <span className="text-truncate" style={{ maxWidth: 160 }}>
                {first.name || "-"}
              </span>
              {extra > 0 && (
                <span className="badge bg-light text-dark">+{extra}</span>
              )}
            </div>
          );
        },
      },
      {
        title: window.translate("Customer"),
        dataIndex: "customer",
        sorter: (a, b) => (a.customer || "").localeCompare(b.customer || ""),
      },
      {
        title: window.translate("Items"),
        dataIndex: "itemsCount",
        sorter: (a, b) => Number(a.itemsCount) - Number(b.itemsCount),
        render: (v) => Number(v || 0).toLocaleString(),
      },
      {
        title: window.translate("Total"),
        dataIndex: "totalAmount",
        sorter: (a, b) => Number(a.totalAmount) - Number(b.totalAmount),
        render: (v) => Number(v || 0).toLocaleString(),
      },
      {
        title: window.translate("Received"),
        dataIndex: "receivedAmount",
        sorter: (a, b) => Number(a.receivedAmount) - Number(b.receivedAmount),
        render: (v) => Number(v || 0).toLocaleString(),
      },
      {
        title: window.translate("Paying"),
        dataIndex: "payingAmount",
        sorter: (a, b) => Number(a.payingAmount) - Number(b.payingAmount),
        render: (v) => Number(v || 0).toLocaleString(),
      },
      {
        title: window.translate("Change"),
        dataIndex: "change",
        sorter: (a, b) => Number(a.change) - Number(b.change),
        render: (v) => Number(v || 0).toLocaleString(),
      },
      {
        title: window.translate("Payment Receiver"),
        dataIndex: "paymentReceiver",
        sorter: (a, b) =>
          (a.paymentReceiver || "").localeCompare(b.paymentReceiver || ""),
      },
      {
        title: window.translate("Payment Note"),
        dataIndex: "paymentNote",
        render: (txt) =>
          txt && txt.length > 40 ? txt.slice(0, 40) + "…" : txt || "-",
        sorter: (a, b) =>
          (a.paymentNote || "").localeCompare(b.paymentNote || ""),
      },
      {
        title: window.translate("Date"),
        dataIndex: "createdAt",
        sorter: (a, b) => {
          const ax = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bx = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return ax - bx;
        },
        render: (d) =>
          d ? new Date(d).toLocaleString() : window.translate("N/A"),
      },
    ],
    [L.product],
  );

  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      {L.exportTooltip}
    </Tooltip>
  );
  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      {window.translate("Refresh")}
    </Tooltip>
  );
  const renderCollapseTooltip = (props) => (
    <Tooltip id="collapse-tooltip" {...props}>
      {window.translate("Collapse")}
    </Tooltip>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>{window.translate("Transactions")}</h4>
              <h6>{window.translate("POS transactions list")}</h6>
            </div>
          </div>

          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.reload();
                  }}
                >
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  to="#"
                  id="collapse-header"
                  className={headerCollapsed ? "active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    dispatch(setToogleHeader(!headerCollapsed));
                  }}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
        </div>

        <div className="card table-list-card">
          {/* 🔹 Toolbar just above the table: search + export side by side */}
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div className="d-flex align-items-center gap-2">
              {/* <div className="input-icon-start search-pos position-relative">
                <span className="input-icon-addon">
                  <i className="ti ti-search" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  style={{ minWidth: 240 }}
                  placeholder={L.search}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div> */}

              {canExportTransactions && (
                <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                  <button
                    type="button"
                    onClick={handleExportExcel}
                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
                  >
                    <Download className="me-1" />
                    <span className="d-none d-sm-inline">{L.export}</span>
                  </button>
                </OverlayTrigger>
              )}
            </div>

            <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3" />
          </div>

          <div className="card-body">
            <div className="table-responsive">
              {loading ? (
                <p>{window.translate("Loading")}…</p>
              ) : filteredRows.length ? (
                <Table
                  columns={columns}
                  dataSource={filteredRows}
                  rowKey={(r) => r.id}
                />
              ) : (
                <p className="p-2">{window.translate("No data available")}.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
